'use client'

import { createContext, useContext, useMemo } from 'react'

import { LocalityService } from '@/services/locality-service'
import { LocalityModel } from '@/types/LocalityModel'

/**
 * Porte le `LocalityService` deja resolu cote serveur jusqu'aux composants
 * clients, au lieu que chacun le reconstruise a partir de `usePathname()`.
 *
 * L'ancien schema — `new LocalityService(usePathname())` repete dans une
 * dizaine de composants `'use client'` — obligeait le bundle client a embarquer
 * les listes de localites belges et luxembourgeoises pour faire la recherche
 * par id. Le `LocalityModel` resolu transite maintenant par la charge RSC
 * (quelques centaines d'octets) et les donnees restent cote serveur.
 *
 * Valeur par defaut : un service sans localite. Hors des routes /localites,
 * `replaceTags` levait deja une erreur avec l'ancien code (la localite y etait
 * `undefined`) — le comportement est donc inchange.
 */
const LocalityContext = createContext<LocalityService>(
  new LocalityService(undefined, undefined),
)

export const LocalityProvider = ({
  slug,
  localite,
  children,
}: {
  /** Chemin de la page, tel que `usePathname()` le renvoyait auparavant. */
  slug: string | undefined
  localite: LocalityModel | undefined
  children: React.ReactNode
}) => {
  const service = useMemo(
    () => new LocalityService(slug, localite),
    [slug, localite],
  )

  return (
    <LocalityContext.Provider value={service}>
      {children}
    </LocalityContext.Provider>
  )
}

export const useLocalityService = () => useContext(LocalityContext)
